import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { staffRosterAPI } from '../../services/api';
import { useUser } from '../../context/UserContext';
import './StaffRosterManagement.css';

const emptyMember = {
  employeeName: '',
  phone: '',
  routingNumber: '',
  circleId: '',
  positionTitle: '',
  rankId: '',
  salaryOverride: '',
  useDefaultSalary: true,
  employmentStatus: 'active',
  notes: ''
};

const emptyRank = {
  name: '',
  scriptRank: '',
  defaultSalary: '',
  displayOrder: 0,
  active: true
};

const emptyCircle = {
  name: '',
  description: '',
  displayOrder: 0,
  active: true
};

const emptyApproval = {
  staffMemberId: '',
  employeeName: '',
  alternateCharacterName: '',
  alternateFactionName: '',
  factionDetails: '',
  approvalStatus: 'pending',
  reviewedByName: '',
  notes: ''
};

const employmentStatuses = [
  'active',
  'inactive',
  'suspended',
  'resigned',
  'terminated',
  'archived'
];

const money = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
});

const formatMoney = (value) => money.format(Number(value || 0));

const formatDate = (value) => {
  if (!value) return 'Not recorded';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Not recorded' : date.toLocaleString();
};

const titleCase = (value) => String(value || '')
  .replace(/_/g, ' ')
  .replace(/\b\w/g, (letter) => letter.toUpperCase());

const parseJson = (value) => {
  if (!value) return null;
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const activityChanges = (entry) => {
  const previous = parseJson(entry.previous_values) || {};
  const next = parseJson(entry.new_values) || {};
  const ignored = new Set([
    'created_at',
    'updated_at',
    'created_by',
    'updated_by',
    'circle_display_order'
  ]);
  return Array.from(new Set([...Object.keys(previous), ...Object.keys(next)]))
    .filter((key) => !ignored.has(key))
    .filter((key) => JSON.stringify(previous[key]) !== JSON.stringify(next[key]))
    .slice(0, 10)
    .map((key) => ({
      key,
      previous: previous[key],
      next: next[key]
    }));
};

const StatusBadge = ({ value, tone }) => (
  <span className={`staff-roster-badge ${tone || String(value || '').toLowerCase()}`}>
    {titleCase(value || 'Unknown')}
  </span>
);

const StaffRosterManagement = () => {
  const { hasPermission } = useUser();
  const canView = hasPermission('staff_roster.view');
  const canCreate = hasPermission('staff_roster.create');
  const canEdit = hasPermission('staff_roster.edit');
  const canArchive = hasPermission('staff_roster.archive');
  const canDelete = hasPermission('staff_roster.delete');
  const canAssignCompensation = hasPermission('staff_roster.assign_compensation');
  const canManageStructure = hasPermission('staff_roster.manage_structure');
  const canReviewPending = hasPermission('staff_roster.review_pending');
  const canManageApprovals = hasPermission('staff_roster.manage_approvals');
  const canViewSummaries = hasPermission('staff_roster.view_summaries');
  const canViewHistory = hasPermission('staff_roster.view_history');

  const views = useMemo(() => [
    canView && { id: 'roster', label: 'Staff Roster' },
    canReviewPending && { id: 'pending', label: 'Pending Assignment' },
    canManageStructure && { id: 'structure', label: 'Ranks & Circles' },
    canViewSummaries && { id: 'summaries', label: 'Circle Summaries' },
    canManageApprovals && { id: 'approvals', label: 'Alternate Factions' },
    canViewHistory && { id: 'history', label: 'Activity History' }
  ].filter(Boolean), [
    canView,
    canReviewPending,
    canManageStructure,
    canViewSummaries,
    canManageApprovals,
    canViewHistory
  ]);

  const [view, setView] = useState(views[0]?.id || 'roster');
  const [members, setMembers] = useState([]);
  const [ranks, setRanks] = useState([]);
  const [circles, setCircles] = useState([]);
  const [pending, setPending] = useState([]);
  const [summary, setSummary] = useState(null);
  const [approvals, setApprovals] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const [memberFormOpen, setMemberFormOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [memberForm, setMemberForm] = useState(emptyMember);

  const [pendingFormOpen, setPendingFormOpen] = useState(false);
  const [editingPending, setEditingPending] = useState(null);
  const [pendingForm, setPendingForm] = useState(emptyMember);
  const [linkMemberId, setLinkMemberId] = useState('');
  const [showResolvedPending, setShowResolvedPending] = useState(false);

  const [editingRank, setEditingRank] = useState(null);
  const [rankForm, setRankForm] = useState(emptyRank);
  const [editingCircle, setEditingCircle] = useState(null);
  const [circleForm, setCircleForm] = useState(emptyCircle);

  const [approvalFormOpen, setApprovalFormOpen] = useState(false);
  const [editingApproval, setEditingApproval] = useState(null);
  const [approvalForm, setApprovalForm] = useState(emptyApproval);
  const [showArchivedApprovals, setShowArchivedApprovals] = useState(false);

  const [filters, setFilters] = useState({
    search: '',
    circleId: '',
    rankId: '',
    status: '',
    sort: 'circle',
    direction: 'asc',
    includeArchived: false
  });
  const [approvalFilters, setApprovalFilters] = useState({ search: '', status: '' });

  const flash = (message) => {
    setNotice(message);
    window.setTimeout(() => setNotice(''), 4000);
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const requests = [
        ['config', staffRosterAPI.getConfig()]
      ];
      if (canView) requests.push(['members', staffRosterAPI.getMembers({ includeArchived: true })]);
      if (canReviewPending) requests.push(['pending', staffRosterAPI.getPending(true)]);
      if (canViewSummaries) requests.push(['summary', staffRosterAPI.getSummary()]);
      if (canManageApprovals) {
        requests.push(['approvals', staffRosterAPI.getApprovals({ includeArchived: true })]);
      }
      if (canViewHistory) requests.push(['history', staffRosterAPI.getHistory()]);

      const results = await Promise.all(requests.map(async ([key, request]) => [key, await request]));
      results.forEach(([key, response]) => {
        if (key === 'config') {
          setCircles(response.data?.circles || []);
          setRanks(response.data?.ranks || []);
        } else if (key === 'members') {
          setMembers(response.data || []);
        } else if (key === 'pending') {
          setPending(response.data || []);
        } else if (key === 'summary') {
          setSummary(response.data || null);
        } else if (key === 'approvals') {
          setApprovals(response.data || []);
        } else if (key === 'history') {
          setHistory(response.data || []);
        }
      });
    } catch (loadError) {
      console.error('Failed to load Staff Roster:', loadError);
      setError(loadError.message || 'Failed to load the Staff Roster.');
    } finally {
      setLoading(false);
    }
  }, [
    canView,
    canReviewPending,
    canViewSummaries,
    canManageApprovals,
    canViewHistory
  ]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!views.some((item) => item.id === view) && views[0]) {
      setView(views[0].id);
    }
  }, [view, views]);

  const filteredMembers = useMemo(() => {
    const search = filters.search.trim().toLowerCase();
    const selectedCircle = String(filters.circleId);
    const selectedRank = String(filters.rankId);
    const visible = members.filter((member) => {
      if (!filters.includeArchived && member.archived_at) return false;
      if (selectedCircle === 'unassigned' && member.circle_id) return false;
      if (
        selectedCircle &&
        selectedCircle !== 'unassigned' &&
        String(member.circle_id || '') !== selectedCircle
      ) return false;
      if (selectedRank && String(member.rank_id || '') !== selectedRank) return false;
      if (filters.status && member.employment_status !== filters.status) return false;
      if (!search) return true;
      return [
        member.employee_name,
        member.phone,
        member.routing_number,
        member.circle_name,
        member.position_title,
        member.rank_name,
        member.notes
      ].some((value) => String(value || '').toLowerCase().includes(search));
    });

    const direction = filters.direction === 'desc' ? -1 : 1;
    const getValue = (member) => {
      if (filters.sort === 'name') return member.employee_name || '';
      if (filters.sort === 'rank') return Number(member.script_rank || 0);
      if (filters.sort === 'salary') return Number(member.effective_salary || 0);
      if (filters.sort === 'status') return member.employment_status || '';
      return member.circle_name || 'zzzz Unassigned';
    };
    return [...visible].sort((a, b) => {
      const first = getValue(a);
      const second = getValue(b);
      if (typeof first === 'number' && typeof second === 'number') {
        return (first - second) * direction || a.employee_name.localeCompare(b.employee_name);
      }
      return String(first).localeCompare(String(second)) * direction ||
        a.employee_name.localeCompare(b.employee_name);
    });
  }, [members, filters]);

  const visiblePending = useMemo(
    () => pending.filter((entry) => (
      showResolvedPending ||
      entry.review_status === 'pending' ||
      entry.review_status === 'confirmed_distinct'
    )),
    [pending, showResolvedPending]
  );

  const visibleApprovals = useMemo(() => {
    const search = approvalFilters.search.trim().toLowerCase();
    return approvals.filter((approval) => {
      if (!showArchivedApprovals && approval.archived_at) return false;
      if (approvalFilters.status && approval.approval_status !== approvalFilters.status) return false;
      if (!search) return true;
      return [
        approval.employee_name,
        approval.alternate_character_name,
        approval.alternate_faction_name,
        approval.reviewed_by_name
      ].some((value) => String(value || '').toLowerCase().includes(search));
    });
  }, [approvals, approvalFilters, showArchivedApprovals]);

  const openNewMember = () => {
    setEditingMember(null);
    setMemberForm(emptyMember);
    setMemberFormOpen(true);
  };

  const openEditMember = (member) => {
    setEditingMember(member);
    setMemberForm({
      employeeName: member.employee_name || '',
      phone: member.phone || '',
      routingNumber: member.routing_number || '',
      circleId: member.circle_id || '',
      positionTitle: member.position_title || '',
      rankId: member.rank_id || '',
      salaryOverride: member.salary_override ?? '',
      useDefaultSalary: member.salary_override === null,
      employmentStatus: member.employment_status || 'active',
      notes: member.notes || ''
    });
    setMemberFormOpen(true);
  };

  const closeMemberForm = () => {
    setMemberFormOpen(false);
    setEditingMember(null);
    setMemberForm(emptyMember);
  };

  const saveMember = async (event, confirmDistinct = false) => {
    event?.preventDefault();
    const payload = {
      employeeName: memberForm.employeeName,
      phone: memberForm.phone,
      routingNumber: memberForm.routingNumber,
      circleId: memberForm.circleId,
      positionTitle: memberForm.positionTitle,
      rankId: memberForm.rankId,
      salaryOverride: memberForm.useDefaultSalary ? null : memberForm.salaryOverride,
      employmentStatus: memberForm.employmentStatus,
      notes: memberForm.notes,
      confirmDistinct
    };

    try {
      setSaving(true);
      setError('');
      const response = editingMember
        ? await staffRosterAPI.updateMember(editingMember.id, payload)
        : await staffRosterAPI.createMember(payload);
      closeMemberForm();
      await loadData();
      flash(response.message || 'Staff record saved.');
    } catch (saveError) {
      const duplicate = saveError.response?.data?.data?.possibleDuplicate;
      if (
        duplicate &&
        !editingMember &&
        window.confirm(
          `${saveError.message}\n\nPossible match: ${duplicate.employeeName}\n\nAdd this person anyway?`
        )
      ) {
        await saveMember(null, true);
        return;
      }
      setError(saveError.message || 'Failed to save the staff record.');
    } finally {
      setSaving(false);
    }
  };

  const toggleMemberArchive = async (member) => {
    const archive = !member.archived_at;
    const verb = archive ? 'archive' : 'restore';
    if (!window.confirm(`${titleCase(verb)} ${member.employee_name}?`)) return;
    try {
      setSaving(true);
      const response = await staffRosterAPI.archiveMember(member.id, archive);
      await loadData();
      flash(response.message);
    } catch (actionError) {
      setError(actionError.message || `Failed to ${verb} the staff member.`);
    } finally {
      setSaving(false);
    }
  };

  const deleteMember = async (member) => {
    if (!window.confirm(
      `Permanently remove ${member.employee_name} from the Staff Roster?\n\nThis cannot be undone and will not delete their website account or character.`
    )) return;
    try {
      setSaving(true);
      const response = await staffRosterAPI.deleteMember(member.id);
      await loadData();
      flash(response.message);
    } catch (deleteError) {
      setError(deleteError.message || 'Failed to remove the staff record.');
    } finally {
      setSaving(false);
    }
  };

  const openPending = (entry) => {
    setEditingPending(entry);
    setPendingForm({
      employeeName: entry.employee_name || '',
      phone: entry.phone || '',
      routingNumber: entry.routing_number || '',
      circleId: entry.circle_id || '',
      positionTitle: entry.position_title || '',
      rankId: entry.rank_id || '',
      salaryOverride: entry.salary_override ?? '',
      useDefaultSalary: entry.salary_override === null,
      employmentStatus: entry.employment_status || 'active',
      notes: entry.notes || ''
    });
    setLinkMemberId(entry.possible_duplicate_member_id || '');
    setPendingFormOpen(true);
  };

  const closePending = () => {
    setPendingFormOpen(false);
    setEditingPending(null);
    setPendingForm(emptyMember);
    setLinkMemberId('');
  };

  const savePending = async (event) => {
    event.preventDefault();
    try {
      setSaving(true);
      setError('');
      const response = await staffRosterAPI.updatePending(editingPending.id, {
        employeeName: pendingForm.employeeName,
        phone: pendingForm.phone,
        routingNumber: pendingForm.routingNumber,
        circleId: pendingForm.circleId,
        positionTitle: pendingForm.positionTitle,
        rankId: pendingForm.rankId,
        salaryOverride: pendingForm.useDefaultSalary ? null : pendingForm.salaryOverride,
        employmentStatus: pendingForm.employmentStatus,
        notes: pendingForm.notes
      });
      await loadData();
      flash(response.message);
      const refreshed = response.data;
      setEditingPending(refreshed);
    } catch (saveError) {
      setError(saveError.message || 'Failed to update the pending assignment.');
    } finally {
      setSaving(false);
    }
  };

  const resolvePending = async (action) => {
    const messages = {
      confirm_distinct: 'Confirm that this applicant is a different person from the possible match?',
      link_existing: 'Link this application to the selected existing staff member without overwriting their record?',
      cancel: 'Cancel this pending roster import?'
    };
    if (action === 'link_existing' && !linkMemberId) {
      setError('Choose an existing staff member to link.');
      return;
    }
    if (!window.confirm(messages[action])) return;
    try {
      setSaving(true);
      const response = await staffRosterAPI.resolvePending(
        editingPending.id,
        action,
        action === 'link_existing' ? linkMemberId : undefined
      );
      closePending();
      await loadData();
      flash(response.message);
    } catch (actionError) {
      setError(actionError.message || 'Failed to resolve the pending assignment.');
    } finally {
      setSaving(false);
    }
  };

  const promotePending = async () => {
    if (!window.confirm(`Move ${editingPending.employee_name} into the active Staff Roster?`)) return;
    try {
      setSaving(true);
      setError('');
      const response = await staffRosterAPI.promotePending(editingPending.id);
      closePending();
      await loadData();
      flash(response.message);
    } catch (promoteError) {
      setError(promoteError.message || 'Failed to move the applicant into the roster.');
    } finally {
      setSaving(false);
    }
  };

  const editRank = (rank) => {
    setEditingRank(rank);
    setRankForm({
      name: rank.name || '',
      scriptRank: rank.script_rank || '',
      defaultSalary: rank.default_salary ?? '',
      displayOrder: rank.display_order || 0,
      active: Boolean(rank.active)
    });
  };

  const saveRank = async (event) => {
    event.preventDefault();
    try {
      setSaving(true);
      const response = editingRank
        ? await staffRosterAPI.updateRank(editingRank.id, rankForm)
        : await staffRosterAPI.createRank(rankForm);
      setEditingRank(null);
      setRankForm(emptyRank);
      await loadData();
      flash(response.message);
    } catch (saveError) {
      setError(saveError.message || 'Failed to save the rank.');
    } finally {
      setSaving(false);
    }
  };

  const toggleRank = async (rank) => {
    try {
      setSaving(true);
      const response = await staffRosterAPI.updateRank(rank.id, { active: !rank.active });
      await loadData();
      flash(response.message);
    } catch (saveError) {
      setError(saveError.message || 'Failed to update the rank.');
    } finally {
      setSaving(false);
    }
  };

  const editCircle = (circle) => {
    setEditingCircle(circle);
    setCircleForm({
      name: circle.name || '',
      description: circle.description || '',
      displayOrder: circle.display_order || 0,
      active: Boolean(circle.active)
    });
  };

  const saveCircle = async (event) => {
    event.preventDefault();
    try {
      setSaving(true);
      const response = editingCircle
        ? await staffRosterAPI.updateCircle(editingCircle.id, circleForm)
        : await staffRosterAPI.createCircle(circleForm);
      setEditingCircle(null);
      setCircleForm(emptyCircle);
      await loadData();
      flash(response.message);
    } catch (saveError) {
      setError(saveError.message || 'Failed to save the circle.');
    } finally {
      setSaving(false);
    }
  };

  const toggleCircle = async (circle) => {
    try {
      setSaving(true);
      const response = await staffRosterAPI.updateCircle(circle.id, { active: !circle.active });
      await loadData();
      flash(response.message);
    } catch (saveError) {
      setError(saveError.message || 'Failed to update the circle.');
    } finally {
      setSaving(false);
    }
  };

  const openNewApproval = () => {
    setEditingApproval(null);
    setApprovalForm(emptyApproval);
    setApprovalFormOpen(true);
  };

  const openEditApproval = (approval) => {
    setEditingApproval(approval);
    setApprovalForm({
      staffMemberId: approval.staff_member_id || '',
      employeeName: approval.employee_name || '',
      alternateCharacterName: approval.alternate_character_name || '',
      alternateFactionName: approval.alternate_faction_name || '',
      factionDetails: approval.faction_details || '',
      approvalStatus: approval.approval_status || 'pending',
      reviewedByName: approval.reviewed_by_name || '',
      notes: approval.notes || ''
    });
    setApprovalFormOpen(true);
  };

  const closeApprovalForm = () => {
    setEditingApproval(null);
    setApprovalForm(emptyApproval);
    setApprovalFormOpen(false);
  };

  const saveApproval = async (event) => {
    event.preventDefault();
    try {
      setSaving(true);
      const response = editingApproval
        ? await staffRosterAPI.updateApproval(editingApproval.id, approvalForm)
        : await staffRosterAPI.createApproval(approvalForm);
      closeApprovalForm();
      await loadData();
      flash(response.message);
    } catch (saveError) {
      setError(saveError.message || 'Failed to save the approval request.');
    } finally {
      setSaving(false);
    }
  };

  const toggleApprovalArchive = async (approval) => {
    try {
      setSaving(true);
      const response = await staffRosterAPI.updateApproval(approval.id, {
        archived: !approval.archived_at
      });
      await loadData();
      flash(response.message);
    } catch (actionError) {
      setError(actionError.message || 'Failed to update the approval request.');
    } finally {
      setSaving(false);
    }
  };

  const deleteApproval = async (approval) => {
    if (!window.confirm(`Permanently delete the approval record for ${approval.employee_name}?`)) return;
    try {
      setSaving(true);
      const response = await staffRosterAPI.deleteApproval(approval.id);
      await loadData();
      flash(response.message);
    } catch (deleteError) {
      setError(deleteError.message || 'Failed to remove the approval request.');
    } finally {
      setSaving(false);
    }
  };

  const selectedMemberRank = ranks.find((rank) => String(rank.id) === String(memberForm.rankId));
  const selectedPendingRank = ranks.find((rank) => String(rank.id) === String(pendingForm.rankId));

  if (loading) {
    return (
      <div className="admin-section staff-roster-admin">
        <div className="loading-message">Loading Staff Roster...</div>
      </div>
    );
  }

  return (
    <div className="admin-section staff-roster-admin">
      <div className="admin-section-header staff-roster-header">
        <div>
          <span className="staff-roster-kicker">Out of Character</span>
          <h1 className="admin-section-title">Staff Roster</h1>
          <p className="staff-roster-intro">
            Staffing, assignments, salaries, approvals, and administrative history.
          </p>
        </div>
        {view === 'roster' && canCreate && (
          <button className="admin-button primary" type="button" onClick={openNewMember}>
            Add Staff Member
          </button>
        )}
        {view === 'approvals' && canManageApprovals && (
          <button className="admin-button primary" type="button" onClick={openNewApproval}>
            Add Approval Request
          </button>
        )}
      </div>

      <div className="staff-roster-tabs" role="tablist" aria-label="Staff Roster management">
        {views.map((item) => (
          <button
            key={item.id}
            type="button"
            className={view === item.id ? 'active' : ''}
            onClick={() => setView(item.id)}
          >
            {item.label}
            {item.id === 'pending' && pending.filter((entry) => (
              entry.review_status === 'pending' || entry.review_status === 'confirmed_distinct'
            )).length > 0 && (
              <span className="staff-roster-tab-count">
                {pending.filter((entry) => (
                  entry.review_status === 'pending' || entry.review_status === 'confirmed_distinct'
                )).length}
              </span>
            )}
          </button>
        ))}
      </div>

      {notice && <div className="staff-roster-message success">{notice}</div>}
      {error && <div className="staff-roster-message error">{error}</div>}

      {view === 'roster' && canView && (
        <>
          {memberFormOpen && (
            <form className="staff-roster-form" onSubmit={saveMember}>
              <div className="staff-roster-form-heading">
                <div>
                  <span className="staff-roster-kicker">Roster record</span>
                  <h2>{editingMember ? 'Edit Staff Member' : 'Add Staff Member'}</h2>
                </div>
                <button type="button" className="staff-roster-close" onClick={closeMemberForm} aria-label="Close">
                  ×
                </button>
              </div>
              <div className="staff-roster-form-grid">
                <label className="admin-form-group">
                  <span className="admin-form-label">Employee or Character Name</span>
                  <input
                    className="admin-form-input"
                    value={memberForm.employeeName}
                    onChange={(event) => setMemberForm((current) => ({
                      ...current,
                      employeeName: event.target.value
                    }))}
                    required
                  />
                </label>
                <label className="admin-form-group">
                  <span className="admin-form-label">Phone Number</span>
                  <input
                    className="admin-form-input"
                    value={memberForm.phone}
                    onChange={(event) => setMemberForm((current) => ({
                      ...current,
                      phone: event.target.value
                    }))}
                  />
                </label>
                <label className="admin-form-group">
                  <span className="admin-form-label">Bank Routing</span>
                  <input
                    className="admin-form-input"
                    value={memberForm.routingNumber}
                    onChange={(event) => setMemberForm((current) => ({
                      ...current,
                      routingNumber: event.target.value
                    }))}
                  />
                </label>
                <label className="admin-form-group">
                  <span className="admin-form-label">Circle or Department</span>
                  <select
                    className="admin-form-select"
                    value={memberForm.circleId}
                    onChange={(event) => setMemberForm((current) => ({
                      ...current,
                      circleId: event.target.value
                    }))}
                  >
                    <option value="">Unassigned</option>
                    {circles.filter((circle) => circle.active || circle.id === editingMember?.circle_id).map((circle) => (
                      <option key={circle.id} value={circle.id}>{circle.name}</option>
                    ))}
                  </select>
                </label>
                <label className="admin-form-group">
                  <span className="admin-form-label">Position or Job Title</span>
                  <input
                    className="admin-form-input"
                    value={memberForm.positionTitle}
                    onChange={(event) => setMemberForm((current) => ({
                      ...current,
                      positionTitle: event.target.value
                    }))}
                  />
                </label>
                <label className="admin-form-group">
                  <span className="admin-form-label">Staff Rank</span>
                  <select
                    className="admin-form-select"
                    value={memberForm.rankId}
                    onChange={(event) => setMemberForm((current) => ({
                      ...current,
                      rankId: event.target.value
                    }))}
                    disabled={!canAssignCompensation}
                  >
                    <option value="">Unassigned</option>
                    {ranks.filter((rank) => rank.active || rank.id === editingMember?.rank_id).map((rank) => (
                      <option key={rank.id} value={rank.id}>
                        {rank.name} · {formatMoney(rank.default_salary)}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="admin-form-group">
                  <span className="admin-form-label">Salary or Payout</span>
                  <input
                    className="admin-form-input"
                    type="number"
                    min="0"
                    step="1"
                    value={
                      memberForm.useDefaultSalary
                        ? selectedMemberRank?.default_salary ?? ''
                        : memberForm.salaryOverride
                    }
                    onChange={(event) => setMemberForm((current) => ({
                      ...current,
                      salaryOverride: event.target.value,
                      useDefaultSalary: false
                    }))}
                    disabled={!canAssignCompensation || memberForm.useDefaultSalary}
                  />
                  {canAssignCompensation && (
                    <label className="staff-roster-inline-check">
                      <input
                        type="checkbox"
                        checked={memberForm.useDefaultSalary}
                        onChange={(event) => setMemberForm((current) => ({
                          ...current,
                          useDefaultSalary: event.target.checked,
                          salaryOverride: event.target.checked ? '' : current.salaryOverride
                        }))}
                      />
                      Use rank default
                    </label>
                  )}
                </div>
                <label className="admin-form-group">
                  <span className="admin-form-label">Employment Status</span>
                  <select
                    className="admin-form-select"
                    value={memberForm.employmentStatus}
                    onChange={(event) => setMemberForm((current) => ({
                      ...current,
                      employmentStatus: event.target.value
                    }))}
                  >
                    {employmentStatuses.map((status) => (
                      <option key={status} value={status}>{titleCase(status)}</option>
                    ))}
                  </select>
                </label>
                <label className="admin-form-group staff-roster-form-wide">
                  <span className="admin-form-label">Administrative Notes</span>
                  <textarea
                    className="admin-form-textarea"
                    rows="4"
                    value={memberForm.notes}
                    onChange={(event) => setMemberForm((current) => ({
                      ...current,
                      notes: event.target.value
                    }))}
                  />
                </label>
              </div>
              <div className="admin-form-actions">
                <button className="admin-button primary" type="submit" disabled={saving}>
                  {saving ? 'Saving...' : editingMember ? 'Save Changes' : 'Add to Roster'}
                </button>
                <button className="admin-button" type="button" onClick={closeMemberForm}>Cancel</button>
              </div>
            </form>
          )}

          <div className="staff-roster-filters">
            <label>
              <span>Search</span>
              <input
                value={filters.search}
                onChange={(event) => setFilters((current) => ({
                  ...current,
                  search: event.target.value
                }))}
                placeholder="Name, position, phone, notes"
              />
            </label>
            <label>
              <span>Circle</span>
              <select
                value={filters.circleId}
                onChange={(event) => setFilters((current) => ({
                  ...current,
                  circleId: event.target.value
                }))}
              >
                <option value="">All Circles</option>
                <option value="unassigned">Unassigned</option>
                {circles.map((circle) => (
                  <option key={circle.id} value={circle.id}>{circle.name}</option>
                ))}
              </select>
            </label>
            <label>
              <span>Rank</span>
              <select
                value={filters.rankId}
                onChange={(event) => setFilters((current) => ({
                  ...current,
                  rankId: event.target.value
                }))}
              >
                <option value="">All Ranks</option>
                {ranks.map((rank) => (
                  <option key={rank.id} value={rank.id}>{rank.name}</option>
                ))}
              </select>
            </label>
            <label>
              <span>Status</span>
              <select
                value={filters.status}
                onChange={(event) => setFilters((current) => ({
                  ...current,
                  status: event.target.value
                }))}
              >
                <option value="">All Statuses</option>
                {employmentStatuses.map((status) => (
                  <option key={status} value={status}>{titleCase(status)}</option>
                ))}
              </select>
            </label>
            <label>
              <span>Sort By</span>
              <select
                value={filters.sort}
                onChange={(event) => setFilters((current) => ({
                  ...current,
                  sort: event.target.value
                }))}
              >
                <option value="circle">Circle</option>
                <option value="name">Name</option>
                <option value="rank">Rank</option>
                <option value="salary">Salary</option>
                <option value="status">Status</option>
              </select>
            </label>
            <button
              type="button"
              className="staff-roster-direction"
              onClick={() => setFilters((current) => ({
                ...current,
                direction: current.direction === 'asc' ? 'desc' : 'asc'
              }))}
              title={filters.direction === 'asc' ? 'Ascending' : 'Descending'}
              aria-label={filters.direction === 'asc' ? 'Sort ascending' : 'Sort descending'}
            >
              {filters.direction === 'asc' ? '↑' : '↓'}
            </button>
            <label className="staff-roster-filter-check">
              <input
                type="checkbox"
                checked={filters.includeArchived}
                onChange={(event) => setFilters((current) => ({
                  ...current,
                  includeArchived: event.target.checked
                }))}
              />
              Include archived
            </label>
          </div>

          <div className="staff-roster-table-wrap">
            <table className="admin-table staff-roster-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Contact & Routing</th>
                  <th>Circle</th>
                  <th>Position</th>
                  <th>Rank</th>
                  <th>Salary</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredMembers.length === 0 ? (
                  <tr><td colSpan="8" className="staff-roster-empty-cell">No staff records match these filters.</td></tr>
                ) : filteredMembers.map((member) => (
                  <tr key={member.id}>
                    <td>
                      <strong>{member.employee_name}</strong>
                      {member.notes && <small>{member.notes}</small>}
                    </td>
                    <td>
                      <span>{member.phone || 'No phone'}</span>
                      <small>{member.routing_number || 'No routing'}</small>
                    </td>
                    <td>{member.circle_name || <span className="staff-roster-muted">Unassigned</span>}</td>
                    <td>{member.position_title || <span className="staff-roster-muted">Unassigned</span>}</td>
                    <td>
                      {member.rank_name || <span className="staff-roster-muted">Unassigned</span>}
                      {member.script_rank && <small>Script rank {member.script_rank}</small>}
                    </td>
                    <td>
                      <strong>{formatMoney(member.effective_salary)}</strong>
                      <small>{member.salary_source === 'override' ? 'Individual override' : 'Rank default'}</small>
                    </td>
                    <td><StatusBadge value={member.employment_status} /></td>
                    <td>
                      <div className="staff-roster-row-actions">
                        {canEdit && (
                          <button type="button" onClick={() => openEditMember(member)}>Edit</button>
                        )}
                        {canArchive && (
                          <button type="button" onClick={() => toggleMemberArchive(member)}>
                            {member.archived_at ? 'Restore' : 'Archive'}
                          </button>
                        )}
                        {canDelete && (
                          <button type="button" className="danger" onClick={() => deleteMember(member)}>
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="staff-roster-result-count">
            {filteredMembers.length} of {members.length} roster records
          </div>
        </>
      )}

      {view === 'pending' && canReviewPending && (
        <>
          <div className="staff-roster-view-toolbar">
            <p>Accepted applications remain here until every required roster field is assigned.</p>
            <label className="staff-roster-filter-check">
              <input
                type="checkbox"
                checked={showResolvedPending}
                onChange={(event) => setShowResolvedPending(event.target.checked)}
              />
              Show resolved
            </label>
          </div>

          {pendingFormOpen && editingPending && (
            <form className="staff-roster-form" onSubmit={savePending}>
              <div className="staff-roster-form-heading">
                <div>
                  <span className="staff-roster-kicker">Pending assignment</span>
                  <h2>{editingPending.employee_name}</h2>
                </div>
                <button type="button" className="staff-roster-close" onClick={closePending} aria-label="Close">×</button>
              </div>
              {editingPending.possible_duplicate_member_id && (
                <div className="staff-roster-duplicate-warning">
                  <strong>Possible existing record:</strong>
                  <span>
                    {editingPending.possible_duplicate_name} · {editingPending.duplicate_reason}
                  </span>
                </div>
              )}
              <div className="staff-roster-form-grid">
                <label className="admin-form-group">
                  <span className="admin-form-label">Employee or Character Name</span>
                  <input
                    className="admin-form-input"
                    value={pendingForm.employeeName}
                    onChange={(event) => setPendingForm((current) => ({
                      ...current,
                      employeeName: event.target.value
                    }))}
                    required
                  />
                </label>
                <label className="admin-form-group">
                  <span className="admin-form-label">Phone Number</span>
                  <input
                    className="admin-form-input"
                    value={pendingForm.phone}
                    onChange={(event) => setPendingForm((current) => ({
                      ...current,
                      phone: event.target.value
                    }))}
                  />
                </label>
                <label className="admin-form-group">
                  <span className="admin-form-label">Bank Routing</span>
                  <input
                    className="admin-form-input"
                    value={pendingForm.routingNumber}
                    onChange={(event) => setPendingForm((current) => ({
                      ...current,
                      routingNumber: event.target.value
                    }))}
                  />
                </label>
                <label className="admin-form-group">
                  <span className="admin-form-label">Circle or Department</span>
                  <select
                    className="admin-form-select"
                    value={pendingForm.circleId}
                    onChange={(event) => setPendingForm((current) => ({
                      ...current,
                      circleId: event.target.value
                    }))}
                  >
                    <option value="">Unassigned</option>
                    {circles.filter((circle) => circle.active).map((circle) => (
                      <option key={circle.id} value={circle.id}>{circle.name}</option>
                    ))}
                  </select>
                </label>
                <label className="admin-form-group">
                  <span className="admin-form-label">Position or Job Title</span>
                  <input
                    className="admin-form-input"
                    value={pendingForm.positionTitle}
                    onChange={(event) => setPendingForm((current) => ({
                      ...current,
                      positionTitle: event.target.value
                    }))}
                  />
                </label>
                <label className="admin-form-group">
                  <span className="admin-form-label">Staff Rank</span>
                  <select
                    className="admin-form-select"
                    value={pendingForm.rankId}
                    onChange={(event) => setPendingForm((current) => ({
                      ...current,
                      rankId: event.target.value
                    }))}
                    disabled={!canAssignCompensation}
                  >
                    <option value="">Unassigned</option>
                    {ranks.filter((rank) => rank.active).map((rank) => (
                      <option key={rank.id} value={rank.id}>
                        {rank.name} · {formatMoney(rank.default_salary)}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="admin-form-group">
                  <span className="admin-form-label">Salary or Payout</span>
                  <input
                    className="admin-form-input"
                    type="number"
                    min="0"
                    step="1"
                    value={
                      pendingForm.useDefaultSalary
                        ? selectedPendingRank?.default_salary ?? ''
                        : pendingForm.salaryOverride
                    }
                    onChange={(event) => setPendingForm((current) => ({
                      ...current,
                      salaryOverride: event.target.value,
                      useDefaultSalary: false
                    }))}
                    disabled={!canAssignCompensation || pendingForm.useDefaultSalary}
                  />
                  {canAssignCompensation && (
                    <label className="staff-roster-inline-check">
                      <input
                        type="checkbox"
                        checked={pendingForm.useDefaultSalary}
                        onChange={(event) => setPendingForm((current) => ({
                          ...current,
                          useDefaultSalary: event.target.checked,
                          salaryOverride: event.target.checked ? '' : current.salaryOverride
                        }))}
                      />
                      Use rank default
                    </label>
                  )}
                </div>
                <label className="admin-form-group">
                  <span className="admin-form-label">Employment Status</span>
                  <select
                    className="admin-form-select"
                    value={pendingForm.employmentStatus}
                    onChange={(event) => setPendingForm((current) => ({
                      ...current,
                      employmentStatus: event.target.value
                    }))}
                  >
                    {employmentStatuses.filter((status) => status !== 'archived').map((status) => (
                      <option key={status} value={status}>{titleCase(status)}</option>
                    ))}
                  </select>
                </label>
                <label className="admin-form-group staff-roster-form-wide">
                  <span className="admin-form-label">Administrative Notes</span>
                  <textarea
                    className="admin-form-textarea"
                    rows="4"
                    value={pendingForm.notes}
                    onChange={(event) => setPendingForm((current) => ({
                      ...current,
                      notes: event.target.value
                    }))}
                  />
                </label>
              </div>
              <div className="admin-form-actions staff-roster-pending-actions">
                <button className="admin-button" type="submit" disabled={saving}>Save Assignment</button>
                {canCreate && canAssignCompensation && (
                  <button className="admin-button primary" type="button" onClick={promotePending} disabled={saving}>
                    Move to Active Roster
                  </button>
                )}
              </div>
              {editingPending.possible_duplicate_member_id && (
                <div className="staff-roster-review-actions">
                  <button type="button" onClick={() => resolvePending('confirm_distinct')}>
                    Confirm Different Person
                  </button>
                  <select value={linkMemberId} onChange={(event) => setLinkMemberId(event.target.value)}>
                    <option value="">Select existing staff record</option>
                    {members.map((member) => (
                      <option key={member.id} value={member.id}>{member.employee_name}</option>
                    ))}
                  </select>
                  <button type="button" onClick={() => resolvePending('link_existing')}>
                    Link Existing Record
                  </button>
                </div>
              )}
              <div className="staff-roster-cancel-import">
                <button type="button" onClick={() => resolvePending('cancel')}>Cancel Import</button>
              </div>
            </form>
          )}

          <div className="staff-roster-table-wrap">
            <table className="admin-table staff-roster-table">
              <thead>
                <tr>
                  <th>Applicant</th>
                  <th>Application</th>
                  <th>Assignment</th>
                  <th>Duplicate Check</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visiblePending.length === 0 ? (
                  <tr><td colSpan="6" className="staff-roster-empty-cell">No applicants are awaiting assignment.</td></tr>
                ) : visiblePending.map((entry) => (
                  <tr key={entry.id}>
                    <td>
                      <strong>{entry.employee_name}</strong>
                      <small>{entry.phone || 'No phone'} · {entry.routing_number || 'No routing'}</small>
                    </td>
                    <td>
                      <span>Application #{entry.application_id}</span>
                      <small>Accepted from {entry.position_title || 'Unspecified role'}</small>
                    </td>
                    <td>
                      <span>{entry.circle_name || 'Unassigned circle'}</span>
                      <small>{entry.rank_name || 'Unassigned rank'} · {formatMoney(entry.effective_salary)}</small>
                    </td>
                    <td>
                      {entry.possible_duplicate_member_id ? (
                        <>
                          <StatusBadge value="Review Required" tone="warning" />
                          <small>{entry.possible_duplicate_name} · {entry.duplicate_reason}</small>
                        </>
                      ) : <StatusBadge value="No Match" tone="positive" />}
                    </td>
                    <td><StatusBadge value={entry.review_status} /></td>
                    <td>
                      <div className="staff-roster-row-actions">
                        <button type="button" onClick={() => openPending(entry)}>Review</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {view === 'structure' && canManageStructure && (
        <div className="staff-roster-structure">
          <section>
            <div className="staff-roster-subheading">
              <div>
                <span className="staff-roster-kicker">Compensation</span>
                <h2>Ranks & Salaries</h2>
              </div>
            </div>
            <form className="staff-roster-inline-form" onSubmit={saveRank}>
              <input
                value={rankForm.name}
                onChange={(event) => setRankForm((current) => ({ ...current, name: event.target.value }))}
                placeholder="Rank name"
                required
              />
              <input
                value={rankForm.scriptRank}
                onChange={(event) => setRankForm((current) => ({ ...current, scriptRank: event.target.value }))}
                placeholder="Script rank"
              />
              <input
                type="number"
                min="0"
                step="1"
                value={rankForm.defaultSalary}
                onChange={(event) => setRankForm((current) => ({ ...current, defaultSalary: event.target.value }))}
                placeholder="Default salary"
                required
              />
              <input
                type="number"
                value={rankForm.displayOrder}
                onChange={(event) => setRankForm((current) => ({ ...current, displayOrder: event.target.value }))}
                aria-label="Rank display order"
              />
              <button className="admin-button primary small" type="submit" disabled={saving}>
                {editingRank ? 'Save Rank' : 'Add Rank'}
              </button>
              {editingRank && (
                <button
                  className="admin-button small"
                  type="button"
                  onClick={() => {
                    setEditingRank(null);
                    setRankForm(emptyRank);
                  }}
                >
                  Cancel
                </button>
              )}
            </form>
            <div className="staff-roster-table-wrap">
              <table className="admin-table staff-roster-table">
                <thead>
                  <tr>
                    <th>Rank</th>
                    <th>Script Rank</th>
                    <th>Default Salary</th>
                    <th>Order</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {ranks.map((rank) => (
                    <tr key={rank.id}>
                      <td><strong>{rank.name}</strong></td>
                      <td>{rank.script_rank || 'Not set'}</td>
                      <td>{formatMoney(rank.default_salary)}</td>
                      <td>{rank.display_order}</td>
                      <td><StatusBadge value={rank.active ? 'Active' : 'Disabled'} /></td>
                      <td>
                        <div className="staff-roster-row-actions">
                          <button type="button" onClick={() => editRank(rank)}>Edit</button>
                          <button type="button" onClick={() => toggleRank(rank)}>
                            {rank.active ? 'Disable' : 'Enable'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <div className="staff-roster-subheading">
              <div>
                <span className="staff-roster-kicker">Organisation</span>
                <h2>Circles & Departments</h2>
              </div>
            </div>
            <form className="staff-roster-inline-form circle" onSubmit={saveCircle}>
              <input
                value={circleForm.name}
                onChange={(event) => setCircleForm((current) => ({ ...current, name: event.target.value }))}
                placeholder="Circle name"
                required
              />
              <input
                value={circleForm.description}
                onChange={(event) => setCircleForm((current) => ({ ...current, description: event.target.value }))}
                placeholder="Short description"
              />
              <input
                type="number"
                value={circleForm.displayOrder}
                onChange={(event) => setCircleForm((current) => ({ ...current, displayOrder: event.target.value }))}
                aria-label="Circle display order"
              />
              <button className="admin-button primary small" type="submit" disabled={saving}>
                {editingCircle ? 'Save Circle' : 'Add Circle'}
              </button>
              {editingCircle && (
                <button
                  className="admin-button small"
                  type="button"
                  onClick={() => {
                    setEditingCircle(null);
                    setCircleForm(emptyCircle);
                  }}
                >
                  Cancel
                </button>
              )}
            </form>
            <div className="staff-roster-table-wrap">
              <table className="admin-table staff-roster-table">
                <thead>
                  <tr>
                    <th>Circle</th>
                    <th>Description</th>
                    <th>Order</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {circles.length === 0 ? (
                    <tr><td colSpan="5" className="staff-roster-empty-cell">No circles have been configured.</td></tr>
                  ) : circles.map((circle) => (
                    <tr key={circle.id}>
                      <td><strong>{circle.name}</strong></td>
                      <td>{circle.description || 'No description'}</td>
                      <td>{circle.display_order}</td>
                      <td><StatusBadge value={circle.active ? 'Active' : 'Disabled'} /></td>
                      <td>
                        <div className="staff-roster-row-actions">
                          <button type="button" onClick={() => editCircle(circle)}>Edit</button>
                          <button type="button" onClick={() => toggleCircle(circle)}>
                            {circle.active ? 'Disable' : 'Enable'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )}

      {view === 'summaries' && canViewSummaries && summary && (
        <>
          <div className="staff-roster-stats">
            <div>
              <span>Active Staff</span>
              <strong>{summary.totalActiveMembers}</strong>
            </div>
            <div>
              <span>Pending Assignment</span>
              <strong>{summary.totalPendingAssignments}</strong>
            </div>
            <div>
              <span>Weekly Salary</span>
              <strong>{formatMoney(summary.totalSalary)}</strong>
            </div>
            <div>
              <span>Monthly Salary</span>
              <strong>{formatMoney(summary.totalSalary * 4)}</strong>
            </div>
          </div>
          <div className="staff-roster-summary-sections">
            {summary.circles.length === 0 ? (
              <div className="staff-roster-empty">
                <span className="staff-roster-divider"></span>
                <p>No active staff are available for circle summaries.</p>
              </div>
            ) : summary.circles.map((circle) => (
              <section key={circle.id || 'unassigned'}>
                <div className="staff-roster-summary-heading">
                  <div>
                    <span className="staff-roster-divider"></span>
                    <h2>{circle.name}</h2>
                    <p>{circle.memberCount} active member{circle.memberCount === 1 ? '' : 's'}</p>
                  </div>
                  <strong>{formatMoney(circle.totalSalary)} weekly</strong>
                </div>
                <div className="staff-roster-table-wrap">
                  <table className="admin-table staff-roster-table">
                    <thead>
                      <tr>
                        <th>Employee</th>
                        <th>Position</th>
                        <th>Rank</th>
                        <th>Salary</th>
                      </tr>
                    </thead>
                    <tbody>
                      {circle.members.map((member) => (
                        <tr key={member.id}>
                          <td>{member.employee_name}</td>
                          <td>{member.position_title || 'Unassigned'}</td>
                          <td>{member.rank_name || 'Unassigned'}</td>
                          <td>{formatMoney(member.effective_salary)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            ))}
          </div>
        </>
      )}

      {view === 'approvals' && canManageApprovals && (
        <>
          {approvalFormOpen && (
            <form className="staff-roster-form" onSubmit={saveApproval}>
              <div className="staff-roster-form-heading">
                <div>
                  <span className="staff-roster-kicker">Alternate faction</span>
                  <h2>{editingApproval ? 'Edit Approval Request' : 'New Approval Request'}</h2>
                </div>
                <button type="button" className="staff-roster-close" onClick={closeApprovalForm} aria-label="Close">×</button>
              </div>
              <div className="staff-roster-form-grid">
                <label className="admin-form-group">
                  <span className="admin-form-label">Linked Staff Member</span>
                  <select
                    className="admin-form-select"
                    value={approvalForm.staffMemberId}
                    onChange={(event) => {
                      const member = members.find((item) => String(item.id) === event.target.value);
                      setApprovalForm((current) => ({
                        ...current,
                        staffMemberId: event.target.value,
                        employeeName: member?.employee_name || current.employeeName
                      }));
                    }}
                  >
                    <option value="">No linked record</option>
                    {members.filter((member) => !member.archived_at).map((member) => (
                      <option key={member.id} value={member.id}>{member.employee_name}</option>
                    ))}
                  </select>
                </label>
                <label className="admin-form-group">
                  <span className="admin-form-label">Employee or Character Name</span>
                  <input
                    className="admin-form-input"
                    value={approvalForm.employeeName}
                    onChange={(event) => setApprovalForm((current) => ({
                      ...current,
                      employeeName: event.target.value
                    }))}
                    required
                  />
                </label>
                <label className="admin-form-group">
                  <span className="admin-form-label">Alternate Character Name</span>
                  <input
                    className="admin-form-input"
                    value={approvalForm.alternateCharacterName}
                    onChange={(event) => setApprovalForm((current) => ({
                      ...current,
                      alternateCharacterName: event.target.value
                    }))}
                  />
                </label>
                <label className="admin-form-group">
                  <span className="admin-form-label">Alternate Faction</span>
                  <input
                    className="admin-form-input"
                    value={approvalForm.alternateFactionName}
                    onChange={(event) => setApprovalForm((current) => ({
                      ...current,
                      alternateFactionName: event.target.value
                    }))}
                    required
                  />
                </label>
                <label className="admin-form-group">
                  <span className="admin-form-label">Approval Status</span>
                  <select
                    className="admin-form-select"
                    value={approvalForm.approvalStatus}
                    onChange={(event) => setApprovalForm((current) => ({
                      ...current,
                      approvalStatus: event.target.value
                    }))}
                  >
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="denied">Denied</option>
                  </select>
                </label>
                <label className="admin-form-group">
                  <span className="admin-form-label">Reviewed By Override</span>
                  <input
                    className="admin-form-input"
                    value={approvalForm.reviewedByName}
                    onChange={(event) => setApprovalForm((current) => ({
                      ...current,
                      reviewedByName: event.target.value
                    }))}
                    placeholder="Automatically uses the current administrator"
                  />
                </label>
                <label className="admin-form-group staff-roster-form-wide">
                  <span className="admin-form-label">Faction Details</span>
                  <textarea
                    className="admin-form-textarea"
                    rows="3"
                    value={approvalForm.factionDetails}
                    onChange={(event) => setApprovalForm((current) => ({
                      ...current,
                      factionDetails: event.target.value
                    }))}
                  />
                </label>
                <label className="admin-form-group staff-roster-form-wide">
                  <span className="admin-form-label">Administrative Notes</span>
                  <textarea
                    className="admin-form-textarea"
                    rows="3"
                    value={approvalForm.notes}
                    onChange={(event) => setApprovalForm((current) => ({
                      ...current,
                      notes: event.target.value
                    }))}
                  />
                </label>
              </div>
              <div className="admin-form-actions">
                <button className="admin-button primary" type="submit" disabled={saving}>
                  {saving ? 'Saving...' : 'Save Approval Request'}
                </button>
                <button className="admin-button" type="button" onClick={closeApprovalForm}>Cancel</button>
              </div>
            </form>
          )}

          <div className="staff-roster-filters approvals">
            <label>
              <span>Search</span>
              <input
                value={approvalFilters.search}
                onChange={(event) => setApprovalFilters((current) => ({
                  ...current,
                  search: event.target.value
                }))}
                placeholder="Employee, faction, reviewer"
              />
            </label>
            <label>
              <span>Status</span>
              <select
                value={approvalFilters.status}
                onChange={(event) => setApprovalFilters((current) => ({
                  ...current,
                  status: event.target.value
                }))}
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="denied">Denied</option>
              </select>
            </label>
            <label className="staff-roster-filter-check">
              <input
                type="checkbox"
                checked={showArchivedApprovals}
                onChange={(event) => setShowArchivedApprovals(event.target.checked)}
              />
              Include archived
            </label>
          </div>

          <div className="staff-roster-table-wrap">
            <table className="admin-table staff-roster-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Alternate Character</th>
                  <th>Faction</th>
                  <th>Status</th>
                  <th>Reviewed By</th>
                  <th>Decision</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleApprovals.length === 0 ? (
                  <tr><td colSpan="7" className="staff-roster-empty-cell">No approval records match these filters.</td></tr>
                ) : visibleApprovals.map((approval) => (
                  <tr key={approval.id}>
                    <td>
                      <strong>{approval.employee_name}</strong>
                      {approval.notes && <small>{approval.notes}</small>}
                    </td>
                    <td>{approval.alternate_character_name || 'Not recorded'}</td>
                    <td>
                      <strong>{approval.alternate_faction_name}</strong>
                      {approval.faction_details && <small>{approval.faction_details}</small>}
                    </td>
                    <td><StatusBadge value={approval.approval_status} /></td>
                    <td>{approval.reviewed_by_name || 'Awaiting review'}</td>
                    <td>{formatDate(approval.decided_at)}</td>
                    <td>
                      <div className="staff-roster-row-actions">
                        <button type="button" onClick={() => openEditApproval(approval)}>Edit</button>
                        <button type="button" onClick={() => toggleApprovalArchive(approval)}>
                          {approval.archived_at ? 'Restore' : 'Archive'}
                        </button>
                        <button type="button" className="danger" onClick={() => deleteApproval(approval)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {view === 'history' && canViewHistory && (
        <div className="staff-roster-history">
          {history.length === 0 ? (
            <div className="staff-roster-empty">
              <span className="staff-roster-divider"></span>
              <p>No Staff Roster activity has been recorded.</p>
            </div>
          ) : history.map((entry) => {
            const changes = activityChanges(entry);
            return (
              <article key={entry.id}>
                <div className="staff-roster-history-main">
                  <StatusBadge value={entry.action} />
                  <div>
                    <strong>{entry.entity_label || titleCase(entry.entity_type)}</strong>
                    <span>{entry.actor_username} · {formatDate(entry.created_at)}</span>
                  </div>
                </div>
                {changes.length > 0 && (
                  <details>
                    <summary>{changes.length} recorded change{changes.length === 1 ? '' : 's'}</summary>
                    <div className="staff-roster-change-list">
                      {changes.map((change) => (
                        <div key={change.key}>
                          <strong>{titleCase(change.key)}</strong>
                          <span>{String(change.previous ?? 'Not set')}</span>
                          <span>{String(change.next ?? 'Not set')}</span>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default StaffRosterManagement;
